import { useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { api, mxn, fmtDate } from "@/lib/api";
import { Printer } from "lucide-react";

export default function ReporteConciliacionPrint() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    const qs = params.toString();
    api.get(`/reconciliation${qs ? `?${qs}` : ""}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [params]);

  useEffect(() => {
    document.body.style.background = "#dfe4ea";
    return () => (document.body.style.background = "");
  }, []);

  if (data === null) return <div className="p-10 font-mono-data text-[#5B6672]">Generando reporte…</div>;
  if (data === false) return <div className="p-10 font-mono-data text-[#ED1F26]">No se pudo generar el reporte.</div>;

  const { matched, remisiones_sin_factura, facturas_sin_remision, totals, filters, company } = data;
  const rango = filters.date_from || filters.date_to
    ? `${filters.date_from ? fmtDate(filters.date_from) : "inicio"} — ${filters.date_to ? fmtDate(filters.date_to) : "hoy"}`
    : "Todos los registros";

  return (
    <div className="min-h-screen py-8 flex flex-col items-center">
      <div className="no-print mb-6 flex gap-3">
        <button onClick={() => window.print()} data-testid="print-conciliacion-button"
          className="inline-flex items-center gap-2 bg-[#465B7F] hover:bg-[#374A66] text-white px-5 py-2.5 text-sm uppercase tracking-widest transition-colors">
          <Printer size={16} /> Imprimir conciliación
        </button>
        <button onClick={() => window.close()} className="px-5 py-2.5 text-sm uppercase tracking-widest border border-[#16202B] text-[#16202B] hover:bg-[#16202B] hover:text-white transition-colors">
          Cerrar
        </button>
      </div>

      <div className="bg-white shadow-lg" style={{ width: "279mm", minHeight: "180mm", padding: "16mm" }}>
        <div className="h-1.5 brand-gradient -mx-[16mm] -mt-[16mm] mb-6" />
        <div className="flex justify-between items-start border-b-2 border-[#16202B] pb-4">
          <img src="/tas-logo.webp" alt="TAS Publicidad 360" className="w-44" />
          <div className="text-right">
            <div className="font-display font-bold text-2xl text-[#465B7F]">CONCILIACIÓN REMISIONES ↔ FACTURAS</div>
            <div className="font-mono-data text-xs text-[#5B6672] mt-1">Periodo: {rango}</div>
            <div className="font-mono-data text-xs text-[#5B6672]">Emitido: {fmtDate(new Date().toISOString())}</div>
          </div>
        </div>

        <Block title={`Remisiones con coincidencia (${totals.matched})`} cols={["Remisión", "Factura", "Cliente", "Coincidencia", "Monto"]}>
          {matched.length === 0 ? <EmptyR span={5} /> : matched.map((m) => (
            <tr key={m.remision_id} className="border-b border-[#E3E8EE]">
              <td className="py-2 text-[#16202B]">{m.remision_number}</td>
              <td className="py-2">{m.invoice_number}</td>
              <td className="py-2">{m.client_name || "—"}</td>
              <td className="py-2 uppercase text-xs">{m.match_type}</td>
              <td className="py-2 text-right text-[#16202B]">{mxn(m.remision_total)}</td>
            </tr>
          ))}
        </Block>

        <Block title={`Remisiones sin coincidencia (${totals.remisiones_sin_factura})`} cols={["Remisión", "Cliente", "Monto"]}>
          {remisiones_sin_factura.length === 0 ? <EmptyR span={3} /> : remisiones_sin_factura.map((r) => (
            <tr key={r.remision_id} className="border-b border-[#E3E8EE]">
              <td className="py-2 text-[#16202B]">{r.remision_number}</td>
              <td className="py-2">{r.client_name || "—"}</td>
              <td className="py-2 text-right text-[#16202B]">{mxn(r.remision_total)}</td>
            </tr>
          ))}
        </Block>

        <Block title={`Facturas sin remisión asociada (${totals.facturas_sin_remision})`} cols={["Factura", "Cliente", "Monto"]} critical>
          {facturas_sin_remision.length === 0 ? <EmptyR span={3} /> : facturas_sin_remision.map((f) => (
            <Fragment key={f.invoice_id}>
              <tr className="border-b border-[#E3E8EE]">
                <td className={`py-2 font-semibold ${f.linked ? "text-[#2f7a6c]" : "text-[#ED1F26]"}`}>{f.invoice_number}</td>
                <td className="py-2">{f.client_name || "—"}{f.linked && (f.remision_numbers || []).length ? ` · Rem: ${f.remision_numbers.join(", ")}` : ""}</td>
                <td className={`py-2 text-right ${f.linked ? "text-[#16202B]" : "text-[#ED1F26]"}`}>{mxn(f.total)}</td>
              </tr>
              {(f.candidates || []).map((c) => (
                <tr key={f.invoice_id + c.remision_id} className="border-b border-[#F0F0F0]">
                  <td className="py-1 pl-6 text-xs text-[#5B6672]">↳ posible: {c.remision_number}</td>
                  <td className="py-1 text-xs text-[#5B6672]">{c.client_name || "—"} · {c.match_type}</td>
                  <td className="py-1 text-right text-xs text-[#5B6672]">{mxn(c.total)}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </Block>

        <div className="mt-12 font-mono-data text-xs text-center text-[#5B6672] border-t border-[#E3E8EE] pt-4">
          {company?.name || "TAS Publicidad 360"} · Reporte de auditoría (solo lectura)
        </div>
      </div>
    </div>
  );
}

function Block({ title, cols, critical, children }) {
  return (
    <div className="mt-6">
      <div className={`font-display font-bold text-sm mb-1 ${critical ? "text-[#ED1F26]" : "text-[#16202B]"}`}>{title}</div>
      <table className="w-full font-mono-data text-sm">
        <thead>
          <tr className="border-b-2 border-[#16202B] text-left">
            {cols.map((c, i) => (
              <th key={c} className={`py-2 uppercase text-xs tracking-widest ${i === cols.length - 1 ? "text-right" : ""}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function EmptyR({ span }) {
  return <tr><td colSpan={span} className="py-6 text-center text-[#5B6672]">Sin registros.</td></tr>;
}
